/**
 * AWB-urile comenzii (Victor, 21.10.2026: „in panoul de comenzi sa se adauge si AWB-urile pentru
 * fiecare comanda"). Vin din expeditiile curierilor, legate de comanda prin numarul scris pe AWB.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Truck } from 'lucide-react'
import { api } from '../../lib/api'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import { Pastila } from '../ui/Pastila'
import { Traseu } from '../ui/Traseu'
import type { Ton } from '../ui/TonuriB4L'
import { LinkAwb } from '../ui/LinkAwb'
import { LivratFaraScanare } from '../ui/LivratFaraScanare'

export interface AwbScurt {
  awbNo: string
  courier: string
  stare: string | null
  etichetaStare: string | null
  intarziere: number | null
  deliveredAt: string | null
  inlocuitDe: string | null
  /** Livrat (semnat), dar curierul n-a scanat livrarea in urmarire. */
  faraScanareLivrare?: boolean
  deliveredTo?: string | null
  /** Curierul AWB-ului nou, cand e altul („… (SAMEDAY)" in nota). */
  inlocuitDeCurier?: string | null
}

interface AwbDetaliu extends AwbScurt {
  id: number
  awbDate: string | null
  pickedUpAt: string | null
  pickedUpApprox: boolean
  deliveredTo: string | null
  returnAwbNumber: string | null
  closeNote: string | null
  lastEventName: string | null
  lastEventAt: string | null
  lastEventLocation: string | null
  trackedAt: string | null
  evenimente: { at: string; name: string; location: string | null }[]
}

const TON: Record<string, Ton> = {
  livrat: 'verde',
  in_tranzit: 'albastru',
  in_retur: 'galben',
  refuzat: 'galben',
  retur: 'galben',
  nepreluat: 'gri',
  inchis: 'gri',
  anulat: 'gri',
  despagubit: 'gri',
  inlocuit: 'gri',
  neurmarit: 'gri',
}

const curier = (c: string) => (c === 'SAMEDAY' ? 'Sameday' : c)
const zi = (s: string | null) => (s ? new Date(s).toLocaleDateString('ro-RO') : '—')
const cand = (s: string) =>
  new Date(s).toLocaleString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

/** Starea AWB-ului; intarzierea bate starea, ca la Rambursuri — e ce cere atentie. */
export function StareAwb({ a }: { a: AwbScurt }) {
  // Scurt, ca sa incapa in coloana listei; textul intreg la hover (zilele sunt lucratoare).
  if (a.intarziere != null)
    return (
      <span title={`Întârziat: ${a.intarziere} zile lucrătoare de la preluare`}>
        <Pastila ton="rosu">întârziat {a.intarziere} zile</Pastila>
      </span>
    )
  return <Pastila ton={TON[a.stare ?? ''] ?? 'gri'}>{a.etichetaStare ?? 'în curs'}</Pastila>
}

/** Coloana din lista de comenzi: AWB-ul cel mai relevant, plus cate mai sunt. */
export function AwbInLista({ awb }: { awb: AwbScurt[] }) {
  const primul = awb[0]
  if (!primul) return <span className="text-gray-300">—</span>
  return (
    <div className="grid gap-0.5">
      <span className="font-mono text-xs">
        <LinkAwb curier={primul.courier} awb={primul.awbNo} className="font-mono text-xs" />
        {awb.length > 1 && (
          <span className="ml-1 font-sans text-gray-500" title={awb.map((x) => x.awbNo).join(', ')}>
            +{awb.length - 1}
          </span>
        )}
      </span>
      <span>
        <StareAwb a={primul} />
        {primul.stare === 'livrat' && primul.deliveredAt && (
          <span className="ml-1 text-xs text-gray-500">{zi(primul.deliveredAt)}</span>
        )}
        {primul.faraScanareLivrare && (
          <span className="ml-1">
            <LivratFaraScanare semnat={primul.deliveredTo} />
          </span>
        )}
      </span>
    </div>
  )
}

/** Blocul „Livrare cu curierul” din pagina comenzii. */
export function LivrareComanda({ orderId }: { orderId: number }) {
  const qc = useQueryClient()
  const q = useQuery<{ awb: AwbDetaliu[] }>({
    queryKey: ['orders', orderId, 'awb'],
    queryFn: async () => (await api.get(`/orders/${orderId}/awb`)).data,
  })
  const cauta = useMutation({
    mutationFn: async () =>
      (await api.post(`/orders/${orderId}/awb/cauta`, {}, { timeout: 120_000 })).data as {
        awb: AwbDetaliu[]
      },
    onSuccess: (d) => {
      qc.setQueryData(['orders', orderId, 'awb'], d)
      // Lista de comenzi (cheia `['orders', filtre]`) arata si ea AWB-ul.
      void qc.invalidateQueries({
        queryKey: ['orders'],
        predicate: (x) => x.queryKey[1] !== orderId,
      })
      toast.success(
        d.awb.length === 0
          ? 'Niciun AWB la FAN pentru comanda asta, până acum.'
          : 'AWB-urile sunt la zi.',
      )
    },
    onError: (e: unknown) => {
      const m =
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message ?? 'Nu am putut întreba FAN acum.'
      toast.error(m)
    },
  })
  const awb = q.data?.awb ?? []

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-b4l-blue/10 text-b4l-blue">
            <Truck size={18} />
          </span>
          <div>
            <h2 className="text-base font-semibold tracking-tight">Livrare cu curierul</h2>
            <p className="text-xs text-gray-500">
              AWB-urile care poartă codul comenzii. Se actualizează din oră în oră.
            </p>
          </div>
        </div>
        <Button variant="secondary" onClick={() => cauta.mutate()} loading={cauta.isPending}>
          Caută AWB acum
        </Button>
      </div>

      {q.isLoading ? (
        <p className="text-sm text-gray-500">Se încarcă…</p>
      ) : q.isError ? (
        <p className="text-sm text-red-700">Nu am putut aduce AWB-urile. Încearcă din nou.</p>
      ) : awb.length === 0 ? (
        <p className="text-sm text-gray-500">
          Niciun AWB încă. Apare aici când curierul primește coletul cu codul comenzii pe el; dacă
          AWB-ul a fost emis azi, apasă „Caută AWB acum”.
        </p>
      ) : (
        <div className="grid gap-3">
          {awb.map((a) => (
            <div key={a.id} className="rounded-lg border border-b4l-border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <LinkAwb
                  curier={a.courier}
                  awb={a.awbNo}
                  className="font-mono text-sm font-semibold"
                />
                <span className="text-xs text-gray-500">{curier(a.courier)}</span>
                <StareAwb a={a} />
              </div>
              <dl className="mt-2 grid grid-cols-[120px_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
                <dt className="text-gray-500">AWB emis</dt>
                <dd className="tabular-nums">{zi(a.awbDate)}</dd>
                <dt className="text-gray-500">Preluat</dt>
                <dd className="tabular-nums">
                  {a.pickedUpAt ? zi(a.pickedUpAt) : 'nepreluat încă de curier'}
                  {a.pickedUpApprox && ' (estimat)'}
                </dd>
                {a.deliveredAt && (
                  <>
                    <dt className="text-gray-500">Livrat</dt>
                    <dd className="tabular-nums">
                      {cand(a.deliveredAt)}
                      {a.deliveredTo && `, semnat de ${a.deliveredTo}`}
                      {a.faraScanareLivrare && <LivratFaraScanare semnat={a.deliveredTo} text />}
                    </dd>
                  </>
                )}
                {a.returnAwbNumber && (
                  <>
                    <dt className="text-gray-500">Retur pe AWB</dt>
                    <dd>
                      <LinkAwb
                        curier={a.courier}
                        awb={a.returnAwbNumber}
                        className="font-mono text-xs"
                      />
                    </dd>
                  </>
                )}
                {a.inlocuitDe ? (
                  <>
                    <dt className="text-gray-500">Înlocuit de</dt>
                    <dd>
                      AWB{' '}
                      <LinkAwb
                        curier={a.inlocuitDeCurier ?? a.courier}
                        awb={a.inlocuitDe}
                        className="font-mono text-xs"
                      />{' '}
                      — coletul a plecat pe acela
                    </dd>
                  </>
                ) : (
                  a.closeNote && (
                    <>
                      <dt className="text-gray-500">Închis pentru</dt>
                      <dd>{a.closeNote}</dd>
                    </>
                  )
                )}
              </dl>
              <details className="mt-2">
                <summary className="cursor-pointer text-sm text-b4l-blue">
                  Drumul coletului ({a.evenimente.length})
                </summary>
                <div className="mt-2">
                  <Traseu
                    pasi={a.evenimente.map((e) => ({ cand: e.at, titlu: e.name, loc: e.location }))}
                    gol="Curierul n-a raportat încă nicio mișcare."
                  />
                </div>
              </details>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
